import { memo } from "react";
import { useTranslation } from "react-i18next";

import InviteCustomers from "../InviteCustomers";
import {
  paymentTypes,
  billingPeriod,
  priceTypes,
  canCancelSchedule,
} from "./constants";

import {
  isOnlyNumbers,
  formatDuration,
  formatDate,
  capitalize,
} from "../../utils/helpers";

import Input from "../Input";
import DaysSelector from "../DaysSelector";
import DurationInput from "../DurationInput";
import CustomDatePicker from "../CustomDatePicker";
import CustomSelect from "../Select";
import Weekdays from "../Weekdays";

import { ReactComponent as IndividualIcon } from "../../static/images/individual_icon_green.svg";
import { ReactComponent as EmailIcon } from "../../static/images/email.svg";
import { ReactComponent as PhoneIcon } from "../../static/images/dial-icon.svg";

import "./style.scss";

const IndividualFlow = memo(
  ({
    formData,
    handleFormFieldChange,
    currentStep,
    setInviteInputValue,
    inviteCutomer,
    deleteCustomer,
    invitedCustomers,
    selectedDayAndTime,
    setSelectedDayAndTime,
    errors,
    currency,
  }) => {
    const { t } = useTranslation();

    const weekdaysReviewData = Object.entries(selectedDayAndTime).map(
      ([dayOfWeek, timeOnly]) => ({
        timeOnly,
        dayOfWeek: capitalize(dayOfWeek),
      }),
    );

    return (
      <>
        {currentStep === 2 ? (
          <>
            <h3>{t("createSchedule.common.schedule_days_time_title")}</h3>

            <DaysSelector
              selectedDayAndTime={selectedDayAndTime}
              dateHasError={errors.selectedDays}
              setSelectedDayAndTime={setSelectedDayAndTime}
            />

            <h3>{t("session_details")}</h3>

            <Input
              label={t("createSchedule.common.schedule_name_label")}
              name="scheduleName"
              value={formData.scheduleName}
              onChange={handleFormFieldChange}
              required
              hasError={errors.scheduleName}
              errorText={
                errors.scheduleName
                  ? t("createSchedule.common.input_error_required")
                  : ""
              }
            />

            <DurationInput
              label={t("createSchedule.common.duration_label")}
              onChange={handleFormFieldChange}
              valueHour={formData.durationHours}
              valueMin={formData.durationMin}
              errorfromparent={
                errors.duration
                  ? t("createSchedule.common.input_error_required")
                  : ""
              }
            />

            <>
              <label className="allow-cancel-label">
                {t("createSchedule.individualFlow.allow_cancel_label")}
              </label>
              <div className="radio-buttons-wrapper">
                <label>
                  <input
                    type="radio"
                    name="canCancelSchedule"
                    value={canCancelSchedule.yes}
                    checked={
                      formData.canCancelSchedule === canCancelSchedule.yes
                    }
                    onChange={handleFormFieldChange}
                  />
                  <strong>{t("yes")}</strong>
                </label>
                <label>
                  <input
                    type="radio"
                    name="canCancelSchedule"
                    value={canCancelSchedule.no}
                    checked={
                      formData.canCancelSchedule === canCancelSchedule.no
                    }
                    onChange={handleFormFieldChange}
                  />
                  <strong>{t("no")}</strong>
                </label>
              </div>
              {formData.canCancelSchedule === canCancelSchedule.yes && (
                <Input
                  type="wholeNumber"
                  label={t(
                    "createSchedule.individualFlow.cancelation_grace_info",
                  )}
                  name="cancelationGrace"
                  value={formData.cancelationGrace}
                  onChange={handleFormFieldChange}
                  hasError={errors.cancelationGrace}
                  errorText={
                    errors.cancelationGrace
                      ? t("createSchedule.common.input_error_required")
                      : ""
                  }
                />
              )}
            </>

            <CustomDatePicker
              label={t("createSchedule.common.start_date_label")}
              name="startDate"
              value={formData.startDate}
              onChange={handleFormFieldChange}
              minDate={new Date()}
              error={
                errors.startDate
                  ? t("createSchedule.common.input_error_required")
                  : ""
              }
            />
          </>
        ) : currentStep === 3 ? (
          <>
            <h3>{t("createSchedule.common.price_title")}</h3>
            <div>
              <>
                <div className="radio-buttons-wrapper">
                  <label>
                    <input
                      type="radio"
                      name="priceType"
                      value={priceTypes.monthly}
                      checked={formData.priceType === priceTypes.monthly}
                      onChange={handleFormFieldChange}
                    />
                    <strong>
                      {t("createSchedule.common.price_type_monthly")}
                    </strong>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="priceType"
                      value={priceTypes.perSession}
                      checked={formData.priceType === priceTypes.perSession}
                      onChange={handleFormFieldChange}
                    />
                    <strong>
                      {t("createSchedule.common.price_type_per_session")}
                    </strong>
                  </label>
                </div>
                <Input
                  type="number"
                  label={t("price")}
                  name="price"
                  value={formData.price}
                  onChange={handleFormFieldChange}
                  required
                  hasError={errors.price}
                  errorText={
                    errors.price
                      ? t("createSchedule.common.input_error_required")
                      : ""
                  }
                />
              </>

              <h3>{t("createSchedule.common.billing_period_title")}</h3>
              <CustomSelect
                options={[
                  {
                    label: t("create_invitation_billing_based_on_events"),
                    value: billingPeriod.sessinoBased,
                  },
                  {
                    label: t("create_invitation_billing_month_end"),
                    value: billingPeriod.monthEnd,
                  },
                  {
                    label: t("create_invitation_billing_month_start"),
                    value: billingPeriod.monthStart,
                  },
                ]}
                label={t("select_label_pricing_type")}
                name="billingPeriod"
                value={formData.billingPeriod}
                onChange={handleFormFieldChange}
              />

              {formData.billingPeriod === billingPeriod.sessinoBased && (
                <>
                  <Input
                    type="wholeNumber"
                    name="ofSessionBilling"
                    label={t("createSchedule.common.of_session_billing_info")}
                    value={formData.ofSessionBilling}
                    onChange={handleFormFieldChange}
                    hasError={errors.ofSessionBilling}
                    errorText={
                      errors.ofSessionBilling
                        ? t("createSchedule.common.input_error_required")
                        : ""
                    }
                  />
                  <h3>{t("createSchedule.common.payment_type_title")}</h3>
                  <>
                    <div className="radio-buttons-wrapper">
                      <label>
                        <input
                          type="radio"
                          name="paymentType"
                          value="PostPaid"
                          checked={
                            formData.paymentType === paymentTypes.postPaid
                          }
                          onChange={handleFormFieldChange}
                        />
                        <div>
                          <strong>
                            {t("createSchedule.common.payment_type_postpaid")}
                          </strong>
                          <div>
                            {t(
                              "createSchedule.common.payment_type_postpaid_info",
                            )}
                          </div>
                        </div>
                      </label>
                      <label style={{ marginLeft: "1rem" }}>
                        <input
                          type="radio"
                          name="paymentType"
                          value="PrePaid"
                          checked={
                            formData.paymentType === paymentTypes.prePaid
                          }
                          onChange={handleFormFieldChange}
                        />
                        <div>
                          <strong>
                            {t("createSchedule.common.payment_type_prepaid")}
                          </strong>
                          <div>
                            {t(
                              "createSchedule.common.payment_type_prepaid_info",
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                    {formData.paymentType === paymentTypes.prePaid && (
                      <Input
                        type="wholeNumber"
                        name="ofSessionPrePay"
                        label={t(
                          "createSchedule.common.of_session_pre_pay_info",
                        )}
                        value={formData.ofSessionPrePay}
                        onChange={handleFormFieldChange}
                        hasError={errors.ofSessionPrePay}
                        errorText={
                          errors.ofSessionPrePay
                            ? t("createSchedule.common.input_error_required")
                            : ""
                        }
                      />
                    )}
                  </>
                </>
              )}
            </div>
          </>
        ) : currentStep === 4 ? (
          <InviteCustomers
            setInviteInputValue={setInviteInputValue}
            inviteCutomer={inviteCutomer}
            deleteCustomer={deleteCustomer}
            invitedCustomers={invitedCustomers}
            maxNumber={1}
            errorfromparent={errors.inviteInput}
          />
        ) : (
          <div className="review-section">
            <div className="review-header">
              <IndividualIcon />
              <h4>
                {t("createSchedule.reviewSection.individual.header_title")}
              </h4>
            </div>
            <Weekdays weekdaysData={weekdaysReviewData} />
            <div className="review-date">
              <h4>{t("createSchedule.common.date_timing_title")}</h4>
              <p>{formatDuration(formData.duration)}</p>
              {formData.canCancelSchedule === canCancelSchedule.yes ? (
                <p>
                  {t(
                    "createSchedule.reviewSection.individual.cancelation_allowed",
                    {
                      hours: formData.cancelationGrace,
                    },
                  )}
                </p>
              ) : (
                <p>
                  {t(
                    "createSchedule.reviewSection.individual.cancelation_not_allowed",
                  )}
                </p>
              )}
              <p>
                {t("createSchedule.reviewSection.starting_from", {
                  date: formatDate(formData.startDate, t),
                })}
              </p>
            </div>
            <div className="review-price">
              <h4>{t("createSchedule.reviewSection.pricing_title")}</h4>
              <p>
                {t(
                  formData.priceType === priceTypes.monthly
                    ? "createSchedule.reviewSection.price_per_month"
                    : "createSchedule.reviewSection.price_per_session",
                  { currency, price: formData.price },
                )}
              </p>

              {formData.billingPeriod === billingPeriod.monthEnd ? (
                <p>{t("createSchedule.reviewSection.billed_end_of_month")}</p>
              ) : formData.billingPeriod === billingPeriod.monthStart ? (
                <p>
                  {t("createSchedule.reviewSection.billed_beginning_of_month")}
                </p>
              ) : formData.paymentType === paymentTypes.postPaid ? (
                <p>
                  {t("createSchedule.reviewSection.billed_after_sessions", {
                    count: formData.ofSessionBilling,
                  })}
                </p>
              ) : (
                <p>
                  {t("createSchedule.reviewSection.billed_before_sessions", {
                    count: formData.ofSessionBilling,
                  })}
                </p>
              )}
              {!!formData.ofSessionPrePay && (
                <p>
                  {t("createSchedule.reviewSection.prepaid_after_sessions", {
                    count: formData.ofSessionPrePay,
                  })}
                </p>
              )}
            </div>
            {invitedCustomers.length > 0 && (
              <div className="review-cusomers">
                <h4>{t("createSchedule.common.invited_customers_title")}</h4>
                {invitedCustomers.map((customer) => (
                  <p key={customer.id}>
                    {isOnlyNumbers(customer.contact) ? (
                      <PhoneIcon />
                    ) : (
                      <EmailIcon />
                    )}
                    {customer.contact}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);

export default IndividualFlow;
